
import { supabase } from '@/integrations/supabase/client';
import { ChatGroup } from '@/types/chat';
import { auditService } from '@/services/auditService';

export const supabaseChatGroupService = {
  // Create a new chat group
  async createChatGroup(chatName: string, currentUser?: any, userIds: string[] = []): Promise<ChatGroup> {
    console.log('Creating chat group via Supabase:', chatName);
    console.log('Current user passed to service:', currentUser);
    console.log('User IDs to add:', userIds);
    
    // First try to get Supabase user
    const { data: { user: supabaseUser } } = await supabase.auth.getUser();
    
    // Use the passed user (internal user) if no Supabase user is found
    const user = supabaseUser || currentUser;
    
    if (!user) {
      console.error('No authenticated user found (neither Supabase nor internal)');
      throw new Error('User not authenticated');
    }

    console.log('Using user for chat creation:', user.id);

    // Create the chat group - RLS policies will handle authorization
    const { data, error } = await supabase
      .from('chat_groups')
      .insert({
        chat_name: chatName,
        created_by: user.id
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating chat group:', error);
      throw new Error(`Failed to create chat group: ${error.message}`);
    }

    // Add users to the group if userIds are provided
    if (userIds.length > 0) {
      const groupUserInserts = userIds.map(userId => ({
        chat_group_id: data.chat_id,
        user_id: userId,
        is_active: true
      }));

      const { error: usersError } = await supabase
        .from('chat_group_users')
        .insert(groupUserInserts);

      if (usersError) {
        console.error('Error adding users to chat group:', usersError);
        // Don't throw here, group was created successfully
      }
    }

    // Log the creation
    try {
      await auditService.logAction(
        'CREATE',
        'chat_groups',
        data.chat_id,
        { chat_name: chatName, created_by: user.id, user_count: userIds.length }
      );
    } catch (auditError) {
      console.warn('Failed to log audit action:', auditError);
    }

    console.log('Chat group created successfully:', data);
    return data as ChatGroup;
  },

  // Get chat groups for the current user based on explicit membership
  async getUserChatGroups(currentUser?: any): Promise<ChatGroup[]> {
    console.log('Getting user chat groups via Supabase with explicit membership filtering');
    
    // First try to get Supabase user
    const { data: { user: supabaseUser } } = await supabase.auth.getUser();
    
    // Use the passed user (internal user) if no Supabase user is found
    const user = supabaseUser || currentUser;
    
    if (!user) {
      console.error('No authenticated user found');
      return [];
    }

    console.log('Getting groups for user:', user.id);

    // Get groups where user is creator OR member
    const { data: userMemberships, error: membershipError } = await supabase
      .from('chat_group_users')
      .select('chat_group_id')
      .eq('user_id', user.id)
      .eq('is_active', true);

    if (membershipError) {
      console.error('Error fetching user memberships:', membershipError);
      return [];
    }

    // Get group IDs where user is member
    const memberGroupIds = userMemberships?.map(m => m.chat_group_id) || [];
    
    // Get groups where user is creator OR member
    let query = supabase
      .from('chat_groups')
      .select('*')
      .eq('is_active', true);

    if (memberGroupIds.length > 0) {
      // User is creator OR user is member of these groups
      query = query.or(`created_by.eq.${user.id},chat_id.in.(${memberGroupIds.join(',')})`);
    } else {
      // User is only creator (no memberships)
      query = query.eq('created_by', user.id);
    }

    const { data: groups, error } = await query.order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching chat groups:', error);
      return [];
    }

    if (!groups || groups.length === 0) {
      console.log('No chat groups found for user');
      return [];
    }

    console.log(`Total chat groups found for user ${user.id}:`, groups.length);
    console.log('Groups:', groups.map(g => g.chat_name));
    return groups as ChatGroup[];
  },

  // Delete a chat group (only creators can do this)
  async deleteChatGroup(chatGroupId: string, currentUser?: any): Promise<void> {
    console.log('Deleting chat group via Supabase:', chatGroupId);
    
    // First try to get Supabase user
    const { data: { user: supabaseUser } } = await supabase.auth.getUser();
    
    // Use the passed user (internal user) if no Supabase user is found
    const user = supabaseUser || currentUser;
    
    if (!user) {
      console.error('No authenticated user found');
      throw new Error('User not authenticated');
    }

    // Delete the chat group - RLS policies will ensure only creators can delete
    const { error } = await supabase
      .from('chat_groups')
      .delete()
      .eq('chat_id', chatGroupId)
      .eq('created_by', user.id);

    if (error) {
      console.error('Error deleting chat group:', error);
      throw new Error(`Failed to delete chat group: ${error.message}`);
    }

    // Log the deletion
    try {
      await auditService.logAction(
        'DELETE',
        'chat_groups',
        chatGroupId,
        { deleted_by: user.id }
      );
    } catch (auditError) {
      console.warn('Failed to log audit action:', auditError);
    }

    console.log('Chat group deleted successfully');
  }
};
